import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { GitLabSourceSettingsApi } from "./GitLabSourceSettings.Api";
import type { GitLabSourceSettingsViewModel } from "./GitLabSourceSettings.ViewModel.types";
import type {
  GitLabConfiguration,
  GitLabProject,
} from "./GitLabSourceSettings.types";

const normalizeId = (value: unknown): string => String(value ?? "");

export const useGitLabSourceSettingsViewModel = (
  model: SourceSettingsModel,
): GitLabSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const initialisedConnectionIdRef = useRef<string | null>(null);
  const previousProfileIdentityRef = useRef(
    `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`,
  );

  const [projects, setProjects] = useState<GitLabProject[]>([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.GitLab && model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<GitLabConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  useEffect(() => {
    if (!isVisible) {
      initialisedConnectionIdRef.current = null;
      return;
    }

    if (initialisedConnectionIdRef.current === editingConnectionId) {
      return;
    }

    initialisedConnectionIdRef.current = editingConnectionId;

    updateConnectionConfiguration<GitLabConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        projects: cfg.projects ?? [],
      }),
      { isDefault: true },
    );
  }, [editingConnectionId, isVisible]);

  useEffect(() => {
    const currentProfileIdentity =
      `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previousProfileIdentity === currentProfileIdentity) {
      return;
    }

    setProjects([]);

    updateConnectionConfiguration<GitLabConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        projects: [],
      }),
    );
  }, [
    isVisible,
    model.editingConnection.isAuthorised,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  useEffect(() => {
    const loadProjects = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId) {
        setProjects([]);
        setProjectsLoaded(true);
        return;
      }

      const gitLabApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<GitLabSourceSettingsApi>(
          SourceTypeIds.GitLab,
        );
      if (!gitLabApi) {
        setProjects([]);
        setProjectsLoaded(true);
        return;
      }

      setProjectsLoaded(false);
      setHasError(false);
      try {
        setProjects(await gitLabApi.getProjects(model.boardId, editingConnectionId));
      } catch {
        setProjects([]);
        setHasError(true);
        onUpdateRef.current({ isAuthorised: false });
      } finally {
        setProjectsLoaded(true);
      }
    };

    void loadProjects();
  }, [editingConnectionId, isVisible, model.boardId]);

  return {
    isVisible,
    projects,
    projectOptions: projects.map((project) => ({
      id: normalizeId(project.id),
      label: project.name || normalizeId(project.id),
    })),
    projectsLoaded,
    hasError,
    selectedProjectIds: (configuration.projects ?? []).map((project) =>
      normalizeId(project.id),
    ),
    assignedToMeFixed: true,
    onSelectProjects: (projectIds: string[]) => {
      const selected = projects.filter((project) =>
        projectIds.includes(normalizeId(project.id)),
      );

      updateConnectionConfiguration<GitLabConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          projects: selected,
        }),
      );
    },
  };
};
